// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency } from '@/currency/api/types';
import { ALL_CURRENCIES, IsoCurrencyCode } from '@/currency/constants/iso';
import {
  isZeroDecimal,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import { Decimal } from 'decimal.js';

const NULL_PLACEHOLDER = '—';

// Grouping a BigInt keeps every digit of the integer part exact.
const INTEGER_FORMAT = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const DECIMAL_SEPARATOR =
  new Intl.NumberFormat(undefined)
    .formatToParts(1.5)
    .find((part) => part.type === 'decimal')?.value ?? '.';

// Symbols that do not uniquely identify their currency: shared by multiple
// codes (kr, ¥) or contained in another code's symbol ($ in CA$, R in R$).
const AMBIGUOUS_SYMBOLS: ReadonlySet<string> = (() => {
  const symbols = Object.values(ALL_CURRENCIES).map((info) => info.symbol);
  return new Set(
    symbols.filter(
      (symbol) => symbols.filter((other) => other.includes(symbol)).length > 1
    )
  );
})();

/** Display symbol for an ISO code (e.g. "$", "€", "CA$"). */
export function currencySymbolForIso(currencyCode: IsoCurrencyCode): string {
  return ALL_CURRENCIES[currencyCode].symbol;
}

/** Display symbol for the value's ISO code (e.g. "$", "€", "CA$"). */
export function currencySymbol(value: Currency): string {
  return currencySymbolForIso(value.isoCode);
}

/**
 * Narrow display symbol for an ISO code — drops the disambiguating prefix the
 * full symbol carries (e.g. "CA$" -> "$", "A$" -> "$") while leaving glyph and
 * letter symbols intact ("¥", "CHF", "kr"). For compact layouts where the
 * currency is already identified elsewhere.
 */
export function narrowCurrencySymbol(currencyCode: IsoCurrencyCode): string {
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
  }).formatToParts(0);
  const currencyPart = parts.find((part) => part.type === 'currency');
  return currencyPart?.value ?? ALL_CURRENCIES[currencyCode].symbol;
}

/** Display name for the value's ISO code (e.g. "US dollar"). */
export function currencyLongName(value: Currency): string {
  return ALL_CURRENCIES[value.isoCode].long;
}

/** Dropdown label for an ISO code (e.g. "USD (US dollar)"). */
export function currencyOptionLabel(currencyCode: IsoCurrencyCode): string {
  return `${currencyCode} (${ALL_CURRENCIES[currencyCode].long})`;
}

interface FormatCurrencyValueOptions {
  /** Rendered for null, undefined, and non-finite values. Defaults to an em dash. */
  placeholder?: string;
  /** Omits the ISO suffix an ambiguous symbol carries (e.g. "$1.00", not "$1.00 (USD)"). */
  sparse?: boolean;
}

/** Formats a Currency at full precision with its symbol, padded to the minor unit (e.g. "$1,234.56", "$12.345", "¥1,000"). */
export function formatCurrencyValue(
  value: Currency | null | undefined,
  options?: FormatCurrencyValueOptions
): string {
  const placeholder = options?.placeholder ?? NULL_PLACEHOLDER;
  if (value === null || value === undefined || !value.amount.isFinite()) {
    return placeholder;
  }

  const minimumFractionDigits = isZeroDecimal(value.isoCode) ? 0 : 2;
  const sign = value.amount.isNegative() && !value.amount.isZero() ? '-' : '';
  const [integerDigits, fractionDigits = ''] = value.amount
    .abs()
    .toFixed()
    .split('.');
  // Trailing zeros past the minor unit are dropped, and the minor unit is always shown.
  const displayedFraction = fractionDigits
    .replace(/0+$/, '')
    .padEnd(minimumFractionDigits, '0');
  const groupedInteger = INTEGER_FORMAT.format(BigInt(integerDigits));
  const formatted =
    displayedFraction === ''
      ? groupedInteger
      : `${groupedInteger}${DECIMAL_SEPARATOR}${displayedFraction}`;
  const symbol = currencySymbol(value);
  const isoSuffix =
    options?.sparse !== true && AMBIGUOUS_SYMBOLS.has(symbol)
      ? ` (${value.isoCode})`
      : '';
  return `${sign}${symbol}${formatted}${isoSuffix}`;
}

/**
 * Formats a bare wire amount (a decimal string or Decimal) paired with an
 * invoice's ISO code for display. Invoice line-item and total amounts arrive
 * as bare decimals denominated in the invoice's single currency, so the
 * display sites pair them with that code here. Null, undefined, and
 * unparseable amounts render the placeholder.
 */
export function formatAmountWithCurrency(
  amount: string | Decimal | null | undefined,
  currencyCode: IsoCurrencyCode,
  options?: FormatCurrencyValueOptions
): string {
  if (amount === null || amount === undefined) {
    return options?.placeholder ?? NULL_PLACEHOLDER;
  }
  const parsed = parseCurrencyAmount(String(amount));
  if (parsed === null) {
    return options?.placeholder ?? NULL_PLACEHOLDER;
  }
  return formatCurrencyValue(
    { amount: parsed, isoCode: currencyCode },
    options
  );
}
